import { useCallback, useMemo, useState, ReactNode } from "react";
import { Credit, CreditDocument } from "@/core/entities/credit";
import {
  Card,
  CardHeader,
  Flex,
  Heading,
  Button,
  Icon,
  Icons,
  CardContent,
  Division,
  Table,
  EmptyState,
  Paragraph,
  useToast,
} from "@ir/ui";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { CreditRepository } from "@/infrastructure/repositories/credit";
import { DocumentSignedURLRepository } from "@/infrastructure/repositories/document";
import { DeleteCreditDocumentsUseCase } from "@/core/usecases/credit";
import { GetDocumentSignedURLUseCase } from "@/core/usecases/document";
import CreditDocumentsTableColumns from "./CreditDocumentsTable";
import DocumentUploadModal from "../DocumentUpload/DocumentUploadModal";
import { useCreditDocumentUpload } from "../DocumentUpload/hook";
import { WorkspacePermissions } from "@/core/permissions";
import HasAnyPermission from "@/presentation/workspace/HasAnyPermission";
import { useConfirm, useUserPermissions, useWorkspace, useWindowSize } from "@/hooks";
import { PermissionCheckService } from "@/core/services/PermissionCheck.service";
import { useAtomValue } from "jotai";
import { groupByCheckAtom } from "@/store/deduction/main";
import { currentPageAtom as currentRemittancePageAtom } from "@/store/deduction/remittance";
import { currentPageAtom as currentRemittanceItemPageAtom } from "@/store/deduction/remittanceItem";
import { createLogger } from "@/infrastructure/services/logger";
import { mapCreditDocumentsToViewerDocuments } from "../utils/mapCreditDocumentsToViewerDocuments";

const client = GraphQlAdapter.getInstance().getClient();
const documentSignedURLRepository = new DocumentSignedURLRepository(client);

interface DataType {
  operation?: ReactNode;
}

interface CreditDocumentsProps {
  credit: Credit | null;
  isLoading: boolean;
}

const CreditDocuments = ({ credit, isLoading }: CreditDocumentsProps) => {
  const logger = createLogger("CreditDocuments");
  const toast = useToast();
  const { settings: workspaceSettings } = useWorkspace();
  const { setIsDocumentViewerOpen, setViewerDocuments, isDocumentViewerOpen, setCloseDocumentViewer } = useDeductionDrawer();
  const { width } = useWindowSize();
  const {
    isDocumentUploadOpen,
    openDocumentUpload,
    closeDocumentUpload,
    clearDocumentUpload,
    selectedModalId,
    updateCredit,
  } = useCreditDocumentUpload();
  const [downloadingDocuments, setDownloadingDocuments] = useState<number[]>([]);
  const [deletingDocuments, setDeletingDocuments] = useState<number[]>([]);
  const confirm = useConfirm();
  const permissionCheckService = new PermissionCheckService();
  const { permissions } = useUserPermissions();

  const groupByCheck = useAtomValue(groupByCheckAtom);
  const currentRemittancePage = useAtomValue(currentRemittancePageAtom);
  const currentRemittanceItemPage = useAtomValue(currentRemittanceItemPageAtom);
  const currentPage = groupByCheck ? currentRemittancePage : currentRemittanceItemPage;

  const sortedDocuments = useMemo(() => {
    if (!credit?.documents?.length) return [];
    return [...credit.documents].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [credit?.documents]);

  const fetchDocumentSignedURL = useCallback(
    async (file_url: string, file_type: string, downloadableFileName: string) => {
      return new GetDocumentSignedURLUseCase(documentSignedURLRepository).execute(
        file_url,
        file_type,
        downloadableFileName
      );
    },
    []
  );

  const handleDocumentDownload = useCallback(
    async (creditDocument: CreditDocument) => {
      if (!credit) return;

      setDownloadingDocuments(prev => [...prev, creditDocument.id]);
      if (creditDocument.signedUrl && !creditDocument.isExpired) {
        setDownloadingDocuments(prev => prev.filter(id => id !== creditDocument.id));
        globalThis.open(creditDocument.signedUrl, "_blank");
        return;
      }

      const downloadableFileName = creditDocument.fileDisplayName;
      const { signedUrl } = await fetchDocumentSignedURL(
        creditDocument.filePath,
        creditDocument.fileMimeType,
        downloadableFileName
      );

      setDownloadingDocuments(prev => prev.filter(id => id !== creditDocument.id));
      if (signedUrl) {
        creditDocument.signedUrl = signedUrl;
        globalThis.open(creditDocument.signedUrl, "_blank");
      } else {
        toast.error({
          content: "Failed to download document",
          icon: <Icon name={Icons.File} />,
        });
      }
    },
    [credit, toast, fetchDocumentSignedURL]
  );

  const handleDocumentDelete = async (creditDocumentId: number) => {
    if (!credit) return;

    const confirmResult = await confirm({
      title: "Delete Document",
      message: "Are you sure you want to delete this document?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmResult) {
      return;
    }

    try {
      setDeletingDocuments(prev => [...prev, creditDocumentId]);
      const deleteDocumentUseCase = new DeleteCreditDocumentsUseCase(new CreditRepository(client));
      const response = await deleteDocumentUseCase.execute(
        {
          remittanceItemId: credit.remittanceItemId,
          documentIds: [creditDocumentId],
        },
        currentPage ?? 1
      );

      if (response?.deleteCreditDocuments.remittanceItemId) {
        updateCredit(response.deleteCreditDocuments);
        setViewerDocuments(mapCreditDocumentsToViewerDocuments(response.deleteCreditDocuments.documents));
        toast.success({
          content: "Document deleted",
        });
      } else {
        toast.error({
          content: "Failed to delete document",
        });
      }
    } catch (error) {
      logger.error("Error deleting document", error);
      toast.error({
        content: "Failed to delete document",
      });
    } finally {
      setDeletingDocuments(prev => prev.filter(id => id !== creditDocumentId));
    }
  };

  const columns = CreditDocumentsTableColumns({
    isLoading,
    workspaceSettings,
    downloadingDocuments,
    deletingDocuments,
    handleDocumentDownload,
    handleDocumentDelete,
    hasDeletePermission: permissionCheckService.hasAnyPermission([WorkspacePermissions.CREDIT_DOCUMENT_DELETE], permissions),
  });

  const getSkeletonData = (count: number = 2): CreditDocument[] => {
    const currentDate = new Date().toLocaleDateString();
    return Array.from({ length: count }, (_, index) =>
      CreditDocument.fromJson({
        id: index,
        fileName: `Document ${index + 1}`,
        createdAt: currentDate,
        fileSize: 1000 + index * 1000,
        updatedAt: currentDate,
        documentType: `Document Type ${index + 1}`,
        documentTypeKey: `documentTypeKey ${index + 1}`,
        fileDisplayName: `Document ${index + 1}`,
        fileMimeType: "application/pdf",
        filePath: "",
        deletable: false,
      })
    );
  };

  const handleOpenDocumentViewer = () => {
    setIsDocumentViewerOpen(true);
    setViewerDocuments(mapCreditDocumentsToViewerDocuments(sortedDocuments));
  };

  const handleCloseDocumentViewer = () => {
    setCloseDocumentViewer();
  };

  const openUploadDocument = (remittanceItemId: number) => {
    openDocumentUpload(remittanceItemId);
  };

  const isOpen = selectedModalId != null && selectedModalId === credit?.remittanceItemId && isDocumentUploadOpen;

  const closeUploadDocument = () => {
    closeDocumentUpload();
    clearDocumentUpload();
  };

  const shouldShowUploadButton = !isLoading && Boolean(credit?.remittanceItemId);

  if (!credit && !isLoading) {
    return null;
  }

  return (
    <Card className={`bg-white ${isLoading ? "mb-4" : "mb-3"}`}>
      <CardHeader className="py-2">
        <Flex justifyContent="space-between" align="center">
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Documents
          </Heading>
          <Flex>
            <HasAnyPermission requiredPermissions={[WorkspacePermissions.CREDIT_DOCUMENT_VIEWER]}>
              {width > 1200 && sortedDocuments.length > 0 && !isLoading && (
                <Button
                  size="medium"
                  variant="outline"
                  color="grey"
                  onPress={isDocumentViewerOpen ? handleCloseDocumentViewer : handleOpenDocumentViewer}
                  className="mx-1"
                >
                  <Icon slot="start" name={Icons.File} />
                  {isDocumentViewerOpen ? "Hide Documents" : "View Documents"}
                </Button>
              )}
            </HasAnyPermission>
            <HasAnyPermission requiredPermissions={[WorkspacePermissions.CREDIT_DOCUMENT_ADD]}>
              {shouldShowUploadButton && credit && (
                <Button
                  size="medium"
                  variant="outline"
                  color="primary"
                  onPress={() => openUploadDocument(credit.remittanceItemId)}
                  className={isLoading ? "button-disabled-tooltip ms-1" : "ms-1"}
                >
                  <Icon slot="start" name={Icons.UploadCloud} />
                  Upload
                </Button>
              )}
            </HasAnyPermission>
          </Flex>
        </Flex>
      </CardHeader>
      <CardContent>
        <Flex>
          <Division className="flex-1">
            {sortedDocuments.length > 0 || isLoading ? (
              <Table<CreditDocument & DataType>
                columns={columns}
                dataSource={isLoading ? getSkeletonData() : sortedDocuments}
                border="row"
                aria-label="Credit documents table"
                sticky={true}
                scroll={{ x: "100%" }}
                className="mb-0"
              />
            ) : (
              <EmptyState
                className="bg-white border-1 border-color-grey-25 py-3 flex-1"
                icon={Icons.File}
                title={
                  <Heading level={6} className="text-grey-700 font-bold mt-3">
                    No documents found
                  </Heading>
                }
                description={
                  <Paragraph className="text-grey-500 font-regular mb-0">
                    This is where you can view credit documents
                  </Paragraph>
                }
              />
            )}
          </Division>
        </Flex>
      </CardContent>
      {credit && <DocumentUploadModal key={credit.remittanceItemId} isOpen={isOpen} onClose={closeUploadDocument} credit={credit} />}
    </Card>
  );
};

export default CreditDocuments;
